import { AlertTitle, Alert as MuiAlert, Snackbar } from '@mui/material'
import PropTypes from 'prop-types'
import { createContext, useCallback, useEffect, useState } from 'react'
import { registerErrorNotifier } from '@/data/services/error/errorNotifier'
import { errorService } from '@/data/services/error/errorService'

const ErrorContext = createContext(null)

export function ErrorProvider({ children }) {
  const [error, setError] = useState(null)
  const [showError, setShowError] = useState(false)

  // Handle a new error
  const handleError = useCallback((err, info = {}) => {
    // Log the error with our error service
    errorService.logError(err, info)

    // Set the error message for display
    const errorMessage = errorService.getErrorMessage(err)
    setError({
      message: errorMessage,
      severity: info.severity || 'error',
      title: info.title || 'Error',
    })

    // Show the error notification
    setShowError(true)
  }, [])

  // Clear the current error
  const clearError = useCallback(() => {
    setShowError(false)
    // Wait for animation to complete before clearing error state
    setTimeout(() => setError(null), 300)
  }, [])

  // Handle explicit error display
  const displayError = useCallback((message, options = {}) => {
    setError({
      message,
      severity: options.severity || 'error',
      title: options.title || 'Error',
    })
    setShowError(true)
  }, [])

  // Let non-React code (e.g. the React Query cache) surface errors here.
  // The cache has already logged the error, so this only displays it.
  useEffect(
    () =>
      registerErrorNotifier((err, info = {}) => {
        setError({
          message: errorService.getErrorMessage(err),
          severity: info.severity || 'error',
          title: info.title || 'Error',
        })
        setShowError(true)
      }),
    []
  )

  // Handle notifications with different severities
  const notify = useCallback((message, options = {}) => {
    setError({
      message,
      severity: options.severity || 'info',
      title: options.title || '',
    })
    setShowError(true)
  }, [])

  // Value provided by context
  const value = {
    error,
    handleError,
    clearError,
    displayError,
    notify,
  }

  return (
    <ErrorContext.Provider value={value}>
      {children}

      {/* Global error notification */}
      {error && (
        <Snackbar
          open={showError}
          autoHideDuration={error.severity === 'error' ? 6000 : 3000}
          onClose={clearError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <MuiAlert elevation={6} variant='filled' severity={error.severity} onClose={clearError}>
            {error.title && <AlertTitle>{error.title}</AlertTitle>}
            {error.message}
          </MuiAlert>
        </Snackbar>
      )}
    </ErrorContext.Provider>
  )
}

ErrorProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
